import { yupResolver } from '@hookform/resolvers/yup';
import { format } from 'date-fns';
import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import getFieldValue from 'react-hook-form/dist/logic/getFieldValue';
import { RouteComponentProps, useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import * as yup from 'yup';

import WysiwygEditorInput from '@legacy-components/forms/standard-fields/WysiwygEditorInput';

import { useCreateFrequentPassageStickerType } from '../../../../hooks/api/frequent-passage-sticker-types/useCreateFrequentPassageStickerType';
import { useFrequentPassageStickerType } from '../../../../hooks/api/frequent-passage-sticker-types/useFrequentPassageStickerType';
import { useUpdateFrequentPassageStickerTypes } from '../../../../hooks/api/frequent-passage-sticker-types/useUpdateFrequentPassageStickerTypes';
import { Event, FrequentPassageStickerType } from '../../../../hooks/api/utils/types';
import { defaultError, defaultSuccess, setErrorsOnForm } from '../../../../libs/utils';
import { CreateFrequentPassageStickerTypeRequest } from '../../../../services/api/frequentPassageStickerTypes';
import { notify } from '../../../../services/bugsnag';
import WYSIWYG from '../../../forms/fields/WYSIWYG';
import InputContainer from '../../../forms/hookfields/InputContainer';

export type Props = {
  event: Event;
} & RouteComponentProps<{
  frequentPassageStickerTypesId?: string | undefined;
}>;

const schema = yup.object().shape({
  name: yup.string().required().nullable().label('Name'),
  requestDeadlineAt: yup.string().nullable().label('Request deadline'),
  quantity: yup.number().required().nullable().label('Quantity'),
  voucherTemplateHtml: yup.string().required().nullable().label('Voucher template'),
});

export default function FrequentPassageStickerTypesCreate({ event, match }: Props) {
  const frequentPassageStickerTypeId = Number(match.params.frequentPassageStickerTypesId);
  const { data: frequentPassageStickerType } = useFrequentPassageStickerType(frequentPassageStickerTypeId, {
    enabled: !!frequentPassageStickerTypeId,
  });
  const { mutateAsync: createFrequentPassageStickerType } = useCreateFrequentPassageStickerType();
  const { mutateAsync: updateFrequentPassageStickerType } = useUpdateFrequentPassageStickerTypes();
  const history = useHistory();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<CreateFrequentPassageStickerTypeRequest>({
    defaultValues: {
      name: '',
      requestDeadlineAt: '',
      quantity: null,
      voucherTemplateHtml: '',
    },
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (frequentPassageStickerType) {
      setValue('name', frequentPassageStickerType.name);
      setValue(
        'requestDeadlineAt',
        frequentPassageStickerType.requestDeadlineAt
          ? format(new Date(frequentPassageStickerType.requestDeadlineAt), 'yyyy-MM-dd')
          : null
      );
      setValue('quantity', frequentPassageStickerType.quantity);
      setValue('voucherTemplateHtml', frequentPassageStickerType.voucherTemplateHtml);
    } else {
      reset();
    }
  }, [frequentPassageStickerType]);

  const _handleSubmit = async (values: CreateFrequentPassageStickerTypeRequest) => {
    try {
      if (frequentPassageStickerTypeId) {
        await updateFrequentPassageStickerType({
          id: frequentPassageStickerTypeId,
          data: { ...values, eventId: event.id },
        });
        defaultSuccess('Frequent passage sticker type updated!');
      } else {
        await createFrequentPassageStickerType({ data: { ...values, eventId: event.id } });
        defaultSuccess('Frequent passage sticker type created!');
      }
    } catch (e: any) {
      defaultError(`Failed to update frequent passage sticker type.`);

      setErrorsOnForm(setError, e);

      return;
    }

    history.push(`/admin/events/${event.id}/frequent-passage-sticker-types`);
  };

  return (
    <div className="m-portlet">
      <div className="m-portlet__head">
        <div className="m-portlet__head-caption">
          <div className="m-portlet__head-title">
            <span className="m-portlet__head-icon">
              <i className="flaticon-background"></i>
            </span>
            <h3 className="m-portlet__head-text">
              {frequentPassageStickerTypeId ? 'Edit ' : 'Create '}
              sticker type
            </h3>
          </div>
        </div>
      </div>
      <div className="m-portlet__body">
        <form className="m-login__form m-form" onSubmit={handleSubmit(_handleSubmit)}>
          <InputContainer errors={errors} label={'Name'} required={true} name={'name'}>
            <input type="text" className="form-control m-input" id="name" {...register('name')} />
          </InputContainer>

          <InputContainer errors={errors} label={'Quantity'} required={true} name={'quantity'}>
            <input
              type="number"
              min={0}
              step={1}
              className="form-control m-input"
              id="quantity"
              {...register('quantity')}
            />
          </InputContainer>

          <InputContainer errors={errors} label={'Request deadline'} required={false} name={'requestDeadlineAt'}>
            <input
              type="date"
              className="form-control m-input"
              id="requestDeadlineAt"
              {...register('requestDeadlineAt')}
            />
          </InputContainer>

          <InputContainer errors={errors} label={'Voucher template'} required={true} name={'voucherTemplateHtml'}>
            <WysiwygEditorInput
              required
              {...register('voucherTemplateHtml')}
              value={getValues('voucherTemplateHtml') || ''}
              onChange={(value) => setValue('voucherTemplateHtml', value)}
            />
          </InputContainer>

          {!frequentPassageStickerType && (
            <button
              className={
                'btn m-btn btn-primary' + (isSubmitting ? ' m-btn--custom m-loader m-loader--light m-loader--left' : '')
              }
              disabled={isSubmitting}
            >
              Create
            </button>
          )}

          {frequentPassageStickerType && (
            <button
              key="1"
              className={
                'btn m-btn btn-primary' + (isSubmitting ? ' m-btn--custom m-loader m-loader--light m-loader--left' : '')
              }
              disabled={isSubmitting}
            >
              Update
            </button>
          )}

          <span className="m--margin-left-10">
            or{' '}
            <Link to={`/admin/events/${event.id}/frequent-passage-sticker-types`} className="m-link m--font-bold">
              Cancel
            </Link>
          </span>
        </form>
      </div>
    </div>
  );
}
